import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Building2, Mail, Zap, Activity, StickyNote, FileText, User, CheckSquare, Loader2 } from 'lucide-react';
import TasksPanel from './TasksPanel';
import { STAGES, getStageConfig } from './crmConstants';
import { DealOverviewTab } from './components/deal/DealOverviewTab';
import { DealSequencesTab } from './components/deal/DealSequencesTab';
import { DealEmailTab } from './components/deal/DealEmailTab';
import { DealActivityTab } from './components/deal/DealActivityTab';
import { DealNotesTab } from './components/deal/DealNotesTab';
import { DealDocsTab } from './components/deal/DealDocsTab';

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: 'overview', label: 'Overview', icon: User },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'sequences', label: 'Sequences', icon: Zap },
  { id: 'email', label: 'Email', icon: Mail },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'notes', label: 'Notes', icon: StickyNote },
  { id: 'docs', label: 'Docs', icon: FileText },
];

const panelStyle = {
  position: 'fixed', top: 0, right: 0, bottom: 0, width: 560, maxWidth: '100vw',
  background: '#fff', borderLeft: '1px solid #E5E7EB', zIndex: 100,
  boxShadow: '-8px 0 30px rgba(0,0,0,0.1)',
};

const DealProfile = ({ dealId, onClose, onUpdate, team = [], userRole = 'sales_rep' }) => {
  const [deal, setDeal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [dealEmails, setDealEmails] = useState([]);
  const [msStatus, setMsStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchDealData = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/deals/${dealId}`, { withCredentials: true });
      setDeal(data.deal);
      setActivities(data.activities || []);
      setNotes(data.notes || []);
      setDocuments(data.documents || []);
      setEditData(data.deal || {});

      try {
        const emailRes = await axios.get(`${API}/api/admin/deals/${dealId}/emails`, { withCredentials: true });
        setDealEmails(emailRes.data.emails || []);
      } catch {
        setDealEmails([]);
      }
    } catch (err) {
      console.error('Failed to fetch deal:', err);
    }
    setLoading(false);
  }, [dealId]);

  const checkMsStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auth/microsoft/status`, { withCredentials: true });
      setMsStatus(data);
    } catch {
      setMsStatus({ connected: false });
    }
  }, []);

  useEffect(() => {
    if (dealId) {
      setLoading(true);
      fetchDealData();
      checkMsStatus();
    }
  }, [dealId, fetchDealData, checkMsStatus]);

  const updateStage = async (newStage) => {
    try {
      await axios.put(`${API}/api/admin/deals/${dealId}/stage`, { stage: newStage }, { withCredentials: true });
      setDeal(prev => ({ ...prev, stage: newStage }));
      fetchDealData();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to update stage:', err);
    }
  };

  const assignDeal = async (assigneeId) => {
    try {
      await axios.put(`${API}/api/admin/deals/${dealId}/assign`, { assigned_to: assigneeId }, { withCredentials: true });
      fetchDealData();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to assign deal:', err);
    }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/api/admin/deals/${dealId}`, editData, { withCredentials: true });
      setEditing(false);
      fetchDealData();
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error('Failed to update deal:', err);
    }
  };

  const refreshAll = () => {
    fetchDealData();
    if (onUpdate) onUpdate();
  };

  if (loading) {
    return (
      <div style={{ ...panelStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  if (!deal) return null;

  const stageConfig = getStageConfig(deal.stage || 'New Lead');

  return (
    <div data-testid="deal-profile-panel" style={{ ...panelStyle, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{
        padding: '1rem 1.25rem',
        borderBottom: '1px solid #E5E7EB',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
            <h2 style={{
              fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0
            }}>
              {deal.name || 'Unknown'}
            </h2>
            <span style={{
              fontSize: '0.7rem', fontWeight: 600, padding: '2px 8px', borderRadius: 100,
              background: stageConfig.bg, color: stageConfig.color,
            }}>
              {deal.stage || 'New Lead'}
            </span>
          </div>
          {deal.company && (
            <div style={{ fontSize: '0.813rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <Building2 size={14} /> {deal.company}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setEditing(!editing)} data-testid="deal-edit-toggle-btn" style={{
            padding: '6px 10px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
            border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer', color: '#6B7280',
          }}>
            {editing ? 'Cancel' : 'Edit'}
          </button>
          <button onClick={onClose} data-testid="deal-profile-close-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Stage Selector */}
      <div style={{ padding: '0.75rem 1.25rem', borderBottom: '1px solid #E5E7EB', background: '#FAFBFC' }}>
        <div style={{ fontSize: '0.65rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
          Pipeline Stage
        </div>
        <div style={{ display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
          {STAGES.map(stage => {
            const isActive = (deal.stage || 'New Lead') === stage.id;
            return (
              <button
                key={stage.id}
                onClick={() => updateStage(stage.id)}
                data-testid={`profile-stage-${stage.id.replace(/\s+/g, '-').toLowerCase()}`}
                style={{
                  flex: '1 1 auto',
                  minWidth: 70,
                  padding: '0.375rem 0.5rem',
                  borderRadius: 4,
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  border: isActive ? `2px solid ${stage.color}` : '1px solid #E5E7EB',
                  cursor: 'pointer',
                  background: isActive ? stage.bg : '#fff',
                  color: isActive ? stage.color : '#9CA3AF',
                  transition: 'background 0.15s ease, color 0.15s ease',
                }}
              >
                {stage.id}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', overflowX: 'auto' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            data-testid={`profile-tab-${tab.id}`}
            style={{
              flex: '0 0 auto',
              padding: '0.75rem 0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.25rem',
              fontSize: '0.65rem',
              fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? 'var(--teal)' : '#6B7280',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid var(--teal)' : '2px solid transparent',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <tab.icon size={11} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <DealOverviewTab
              deal={deal}
              dealId={dealId}
              team={team}
              userRole={userRole}
              editing={editing}
              editData={editData}
              setEditData={setEditData}
              onSaveEdit={saveEdit}
              onAssign={assignDeal}
              onRefresh={refreshAll}
            />
          </div>
        )}

        {activeTab === 'tasks' && (
          <TasksPanel dealId={dealId} onTaskChange={fetchDealData} />
        )}

        {activeTab === 'sequences' && (
          <DealSequencesTab dealId={dealId} onRefresh={fetchDealData} />
        )}

        {activeTab === 'email' && (
          <DealEmailTab
            deal={deal}
            dealId={dealId}
            msStatus={msStatus}
            dealEmails={dealEmails}
            onRefresh={fetchDealData}
          />
        )}

        {activeTab === 'activity' && <DealActivityTab activities={activities} />}

        {activeTab === 'notes' && (
          <DealNotesTab dealId={dealId} notes={notes} onRefresh={fetchDealData} />
        )}

        {activeTab === 'docs' && (
          <DealDocsTab dealId={dealId} documents={documents} onRefresh={fetchDealData} />
        )}
      </div>
    </div>
  );
};

export default DealProfile;
